import { useQueryState } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { TransfersBadge } from "@/components/shared/app-navigation/sidebar/badges/company";
import { NoRecordsEmpty } from "@/components/shared/data-table/empty-message";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import UploadProgressBox from "@/features/upload-progress-box/UploadProgressBox";
import { InvoiceSmbReviewStatus } from "@/lib/constants/invoiceStatus";
import { useTenant } from "@/providers/session";
import { useDeleteInvoice } from "@/queries/invoices/delete";
import { bankTranfersToPayFilters } from "@/queries/invoices/live-counts/company/transfers-live-counts";
import { useUpdateInvoices } from "@/queries/invoices/update";

import { BankTransfersDataTable as BankTransfersDataTableComponent } from "./bank-transfers-data-table";
import { BankTransfersStatusFilters } from "./bank-transfers-filters";
import { getColumnsForPaid, getColumnsForToPay } from "./columns";

export const BankTransfers = () => {
  const { t } = useTranslation();
  const { hasCostCenters } = useTenant();
  const [tab, setTab] = useQueryState("tab", {
    defaultValue: BankTransfersStatusFilters.TO_PAY,
  });

  const deleteInvoices = useDeleteInvoice();
  const [updateInvoices] = useUpdateInvoices();

  const columns = useMemo(() => {
    if (tab === BankTransfersStatusFilters.TO_PAY) {
      return getColumnsForToPay(hasCostCenters ?? false);
    }
    return getColumnsForPaid(hasCostCenters ?? false);
  }, [tab, hasCostCenters]);

  return (
    <>
      <div className="flex flex-col h-full gap-4">
        <Tabs
          value={tab}
          className="flex flex-1 flex-col w-full h-full"
          onValueChange={(value) => setTab(value as BankTransfersStatusFilters)}
        >
          <TabsList className="pb-4">
            <TabsTrigger
              value={BankTransfersStatusFilters.TO_PAY}
              className="flex items-center py-3 px-6 gap-4 font-medium text-lg"
            >
              {t("payment_states.to_pay")}
              <TransfersBadge />
            </TabsTrigger>
            <TabsTrigger
              value={BankTransfersStatusFilters.PAID}
              className="flex items-center py-3 px-6 gap-2 font-medium text-lg"
            >
              {t("payment_states.paid")}
            </TabsTrigger>
          </TabsList>
          <TabsContent
            value={BankTransfersStatusFilters.TO_PAY}
            className="flex-1"
          >
            <BankTransfersDataTableComponent
              onDelete={deleteInvoices}
              onPaid={handlePaid}
              tab={BankTransfersStatusFilters.TO_PAY}
              emptyStateComponent={<NoRecordsEmpty state="done" />}
              guidingMessage={t("bank_transfers_to_pay_guiding_message")}
              storageKey={"bank-transfers-to-pay"}
              defaultFilters={bankTranfersToPayFilters}
              columns={columns}
            />
          </TabsContent>
          <TabsContent
            value={BankTransfersStatusFilters.PAID}
            className="flex-1"
          >
            <BankTransfersDataTableComponent
              onDelete={deleteInvoices}
              onPaid={handlePaid}
              tab={BankTransfersStatusFilters.PAID}
              guidingMessage={t("bank_transfers_paid_guiding_message")}
              storageKey={"bank-transfers-paid"}
              defaultFilters={{
                smbReviewStatus: [
                  InvoiceSmbReviewStatus.REVIEWED,
                  InvoiceSmbReviewStatus.REVIEWED_ZERO_TOUCH,
                ],
                isPaid: true,
                wasPaidUsingTabular: true,
              }}
              columns={columns}
            />
          </TabsContent>
        </Tabs>
      </div>
      <UploadProgressBox />
    </>
  );

  function handlePaid(invoiceIds: string[]) {
    updateInvoices(invoiceIds, {
      isPaid: true,
    });
  }
};
